import { Pagination } from "@core/domain/models/pagination";
import { Nullable } from "@core/domain/types/nullable.type";
import { LoadLayoutStore } from "@core/presentacion/component/feedback/load-layout/load-layout.store";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { GlobalEntity } from "@entity/domain/models/global-entity.model";
import { GetAllGlobalEntitiesUseCase } from "@entity/domain/usecases/get-all-global-entities.usecase";
import { Catalogue } from "@project/domain/models/catalogue.model";
import { FinancialEntityType } from "@project/domain/models/financial-entity/financial-entity-type";
import type {
    ProjectListDashboard,
    ProjectListDashboardAlerts,
    ProjectListDashboardExportFormat,
    ProjectListDashboardProject,
    ProjectListDashboardStatusBucket,
    ProjectListDashboardSummary,
} from "@project/domain/models/project-list-dashboard.model";
import { createEmptyProjectListDashboard } from "@project/domain/models/project-list-dashboard.model";
import type { ProjectSearchFilters } from "@project/domain/models/project-search-filters";
import { ProjectsSummary } from "@project/domain/models/projects-summary.model";
import { Status } from "@project/domain/models/status.model";
import { DeleteProjectUseCase } from "@project/domain/usecases/delete-project.usecase";
import { ExportProjectListDashboardUseCase } from "@project/domain/usecases/export-project-list-dashboard.usecase";
import { ExportProjectsUseCase } from "@project/domain/usecases/export-projects.usecase";
import { GetAllFinancialEntityTypesUsecase } from "@project/domain/usecases/financial-entity/get-all-financial-entity-types.usecase";
import { GetAllCataloguesUseCase } from "@project/domain/usecases/get-all-catalogues.usecase";
import { GetAllStatusUseCase } from "@project/domain/usecases/get-all-status.usecase";
import { GetProjectListDashboardUseCase } from "@project/domain/usecases/get-project-list-dashboard.usecase";
import { SearchProjectsByUseCase } from "@project/domain/usecases/search-projects-by.usecase";
import { inject, injectable } from "inversify";
import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from "mobx";

export interface ProjectListTable {
    id: number;
    name: string;
    financialEntitiesList: string;
    status: string;
}

export interface ProjectsListTable {
    projects: ProjectListTable[];
    count: number;
}

@injectable()
export class ProjectListPageViewModel extends BaseViewModel {
    entityId: Nullable<number> = null;

    @observable
    initialLoading: boolean = true;

    @observable
    private _projects: ProjectsSummary = new ProjectsSummary([], 0);

    @observable
    private _dashboard: ProjectListDashboard =
        createEmptyProjectListDashboard();

    @computed
    get projectsTable(): ProjectsListTable {
        return {
            count: this._projects.count,
            projects: this._projects.projectsSummary.map((projectSummary) => ({
                id: projectSummary.id,
                name: projectSummary.name,
                financialEntitiesList:
                    projectSummary.financialEntityTypeLabels.join(", "),
                status: projectSummary.statusLabel,
            })),
        };
    }

    @observable
    status: Status[] = [];

    @observable
    catalogues: Catalogue[] = [];

    @observable
    globalEntities: GlobalEntity[] = [];

    @observable
    financialEntityTypes: FinancialEntityType[] = [];

    @observable
    pagination: Pagination = new Pagination();

    initialFiltersValue: ProjectSearchFilters = {
        name: "",
        catalogue: null,
        financialEntityType: null,
        statusProject: null,
        activeFromDate: null,
        activeToDate: null,
        phase: null,
        priority: null,
        continuity: null,
    };

    @observable
    filters: ProjectSearchFilters = this.initialFiltersValue;

    @computed
    get summary(): ProjectListDashboardSummary {
        return this._dashboard.summary;
    }

    @computed
    get statusOverview(): ProjectListDashboardStatusBucket[] {
        return this._dashboard.statusOverview;
    }

    @computed
    get alerts(): ProjectListDashboardAlerts {
        return this._dashboard.alerts;
    }

    @computed
    get dashboardProjects(): ProjectListDashboardProject[] {
        return this._dashboard.projects;
    }

    @computed
    get dashboardProjectById(): Map<number, ProjectListDashboardProject> {
        return new globalThis.Map(
            this.dashboardProjects.map((project) => [project.id, project]),
        );
    }

    constructor(
        @inject(SearchProjectsByUseCase)
        private readonly searchProjectsByUseCase: SearchProjectsByUseCase,
        @inject(GetProjectListDashboardUseCase)
        private readonly getProjectListDashboardUseCase: GetProjectListDashboardUseCase,
        @inject(GetAllStatusUseCase)
        private readonly getAllStatusUseCase: GetAllStatusUseCase,
        @inject(GetAllCataloguesUseCase)
        private readonly getAllCataloguesUseCase: GetAllCataloguesUseCase,
        @inject(GetAllFinancialEntityTypesUsecase)
        private readonly getFinancialEntityTypesUseCase: GetAllFinancialEntityTypesUsecase,
        @inject(GetAllGlobalEntitiesUseCase)
        private readonly getAllGlobalEntitiesUseCase: GetAllGlobalEntitiesUseCase,
        @inject(ExportProjectsUseCase)
        private readonly exportProjectsUseCase: ExportProjectsUseCase,
        @inject(ExportProjectListDashboardUseCase)
        private readonly exportProjectListDashboardUseCase: ExportProjectListDashboardUseCase,
        @inject(DeleteProjectUseCase)
        private readonly deleteProjectUseCase: DeleteProjectUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        await super.didMount();
        await this.initViewData();
    }

    async initViewData(): Promise<void> {
        await Promise.all([
            this.searchProjectsBy(this.filters, this.pagination),
            this.loadDashboard(),
            this.getAllStatus(),
            this.getAllCatalogues(),
            this.getAllGlobalEntities(),
            this.getFinancialEntityTypes(),
        ]);

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    async exportProjects(): Promise<void> {
        if (!this.entityId) throw Error("Missing entityId");

        LoadLayoutStore.start();

        const blob = await this.exportProjectsUseCase.execute(this.entityId);

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Proyectos.csv");
        document.body.appendChild(link);
        link.click();

        LoadLayoutStore.finish();
    }

    async exportDashboard(
        format: ProjectListDashboardExportFormat,
    ): Promise<void> {
        LoadLayoutStore.start();

        const blob = await this.exportProjectListDashboardUseCase.execute(
            format,
            this.filters,
        );

        const extensionByFormat: Record<
            ProjectListDashboardExportFormat,
            string
        > = {
            csv: "csv",
            xlsx: "xlsx",
            pdf: "pdf",
        };

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
            "download",
            `estado-proyectos.${extensionByFormat[format]}`,
        );
        document.body.appendChild(link);
        link.click();

        LoadLayoutStore.finish();
    }

    async loadDashboard(): Promise<void> {
        const dashboard = await this.getProjectListDashboardUseCase.execute(
            this.filters,
        );

        runInAction(() => {
            this._dashboard = dashboard;
        });
    }

    async getAllCatalogues(): Promise<void> {
        const catalogues = await this.getAllCataloguesUseCase.execute();

        runInAction(() => {
            this.catalogues = catalogues;
        });
    }

    @action
    setFilters(filters: ProjectSearchFilters): void {
        this.filters = filters;
        this.pagination.reset();

        void this.searchProjectsBy(this.filters, this.pagination);
        void this.loadDashboard();
    }

    @action
    setPagination(page: number, pageSize: number): void {
        this.pagination.page = page;
        this.pagination.pageSize = pageSize;

        void this.searchProjectsBy(this.filters, this.pagination);
    }

    async getAllStatus(): Promise<void> {
        const status = await this.getAllStatusUseCase.execute();

        runInAction(() => {
            this.status = status;
        });
    }

    async getAllGlobalEntities(): Promise<void> {
        const globalEntities = await this.getAllGlobalEntitiesUseCase.execute();

        runInAction(() => {
            this.globalEntities = globalEntities;
        });
    }

    async getFinancialEntityTypes(): Promise<void> {
        const financialEntityTypes =
            await this.getFinancialEntityTypesUseCase.execute();

        runInAction(() => {
            this.financialEntityTypes = financialEntityTypes;
        });
    }

    async searchProjectsBy(
        filters: ProjectSearchFilters,
        pagination: Pagination,
    ): Promise<void> {
        const projects = await this.searchProjectsByUseCase.execute(
            pagination,
            filters,
        );
        runInAction(() => {
            this._projects = projects;
        });
    }

    getDashboardProject(
        projectId: number,
    ): ProjectListDashboardProject | undefined {
        return this.dashboardProjectById.get(projectId);
    }

    async deleteProject(projectId: number): Promise<boolean> {
        const ok = await this.deleteProjectUseCase.execute(projectId);
        if (ok) {
            await this.searchProjectsBy(this.filters, this.pagination);
            await this.loadDashboard();
        }
        return ok;
    }
}
